/* ============================================================
   LEFTOVRZ — page transitions (Barba v2.10.3)

   Two transitions, chosen by what you clicked:

   1. "flip"  — a product card lifts its own photograph out of the grid and
                flies it into the product gallery's slot while the rest of
                the page recedes into the ground. Same idiom as the intro's
                wordmark handover: a physical object moving, not a crossfade.

   2. "slam"  — everything else. A full-bleed panel in the brand yellow
                drives up from the bottom to cover, the swap happens behind
                it, and it keeps going off the top. It never reverses
                direction, so it reads as decisive rather than bouncy, and
                it hides the swap completely — no flash of half-built grid.

   Why the existing scripts survive this:
     leftovrz-shop.js and leftovrz-search.js bind delegated listeners to
     `document`, so they keep working across container swaps for free.
     leftovrz-filters.js already exposes window.__lvGridFilters.
     leftovrz-home.js is the only one bound to elements, and it is index
     only — so it gets re-injected when you arrive at the home page.
   ============================================================ */
(() => {
  'use strict';

  const barba = window.barba;
  if (!barba) return;

  /* The theme editor renders sections into the live DOM and drives its own
     navigation. Barba fights both, and the merchant is the one who pays for
     it. Stay out of the editor entirely. */
  if (window.Shopify && window.Shopify.designMode) return;

  const wrapper = document.querySelector('[data-barba="wrapper"]');
  if (!wrapper) return;

  const HOME_JS  = wrapper.dataset.ptHomeJs || '';
  const reduceMQ = matchMedia('(prefers-reduced-motion: reduce)');

  /* Fast out, hard stop, no overshoot — the same decisiveness as the 2px
     borders. A spring here would fight the rest of the page. */
  const EASE  = 'cubic-bezier(.76,0,.24,1)';
  const COVER = 460, CLEAR = 520, FLIP = 620, RECEDE = 260;
  const ms = (n) => (reduceMQ.matches || document.hidden ? 0 : n);

  /* Never await a bare animation.finished, and never await a bare rAF.
     A background tab pauses the document timeline and throttles frames, so
     an animation can reach playState "finished" while its promise is still
     unresolved — and rAF may not fire at all. Awaited directly, either one
     leaves the swap half-done: the old container removed, the new one never
     added, and the page stuck behind the panel until it is reloaded.

     Timers keep firing when a tab is hidden (throttled, but they fire), so
     racing against one guarantees the transition always finishes. */
  const settle = (animation, dur) => Promise.race([
    animation.finished.catch(() => {}),
    new Promise((r) => setTimeout(r, dur + 120)),
  ]);
  const raf2 = () => Promise.race([
    new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r))),
    new Promise((r) => setTimeout(r, 120)),
  ]);

  /* ---------- 1. the slam panel ----------------------------------------
     Built once and parked at translateY(100%). Animated with the Web
     Animations API rather than CSS transitions: a class-driven transition
     needs the element painted first, so every step turns into a forced
     reflow plus class-timing guesswork. WAAPI hands back a promise, which
     is the shape Barba's hooks already want.                             */
  let panel = null;
  const getPanel = () => {
    if (panel) return panel;
    panel = document.createElement('div');
    panel.className = 'pt';
    panel.setAttribute('aria-hidden', 'true');
    /* Read the wordmark off the nav rather than hardcoding an asset, so a
       merchant logo set in the customiser rides the panel too. */
    const src = document.querySelector('.nav__mark img');
    if (src) {
      const mark = document.createElement('img');
      mark.className = 'pt__mark';
      mark.src = src.currentSrc || src.src;
      mark.alt = '';
      panel.appendChild(mark);
    }
    document.body.appendChild(panel);
    return panel;
  };

  /* Set the end state first, then animate from → to. The element is already
     where it belongs when the animation finishes, so there is no fill to
     manage and nothing to cancel afterwards. */
  const slide = (el, from, to, dur) => {
    el.style.transform = to;
    if (reduceMQ.matches) return Promise.resolve();
    return settle(el.animate([{ transform: from }, { transform: to }],
      { duration: ms(dur), easing: EASE }), ms(dur));
  };

  const OFF_DOWN = 'translate3d(0,100%,0)';
  const ON       = 'translate3d(0,0,0)';
  const OFF_UP   = 'translate3d(0,-100%,0)';

  const cover = () => {
    const p = getPanel();
    if (reduceMQ.matches) { p.style.opacity = '1'; p.style.transform = ON; return Promise.resolve(); }
    return slide(p, OFF_DOWN, ON, COVER);
  };
  const uncover = async () => {
    const p = getPanel();
    if (reduceMQ.matches) { p.style.opacity = '0'; p.style.transform = OFF_DOWN; return; }
    await slide(p, ON, OFF_UP, CLEAR);
    p.style.transform = OFF_DOWN;   // re-arm at the bottom for the next run
  };

  /* ---------- 2. what Barba must not touch ------------------------------
     Barba already refuses cross-origin, target=_blank, download links,
     modifier-clicks and the current URL. These are the Shopify ones.       */
  const BLOCKED = new RegExp([
    '/checkouts?(/|$)',              // checkout is its own flow, off-theme
    '/account(/|$)',                 // auth redirects and sets cookies
    /* The whole cart route, not just its write endpoints. This used to be
       handled with cacheIgnore, but @barba/prefetch refuses to install at
       all when cacheIgnore is set — it warns and returns — so keeping the
       cart out that way would have quietly cost every prefetch on the site.
       A real page load for the cart is better anyway: its contents change
       underneath you, and this guarantees they are never served stale. */
    '/cart(/|$)',
    '/(tools|services|apps)/',       // app proxies render their own documents
    '/\\.well-known/',
  ].join('|'), 'i');

  const BLOCKED_EXT = /\.(json|js|xml|atom|txt|pdf|zip|csv|jpe?g|png|webp|gif|svg|mp4|webm)$/i;

  /* Pages that host a review widget do a REAL page load.

     Judge.me — like most review apps — initialises on document load and
     offers no way to re-run. Swapped in over a client-side navigation its
     markup arrives inert: the widget renders at zero height and its write-a-
     review button does nothing, silently. On a resale store the reviews are
     the trust mechanism, so a working widget beats a transition into the
     page that carries it.

     Everything else still transitions: collections, search, the cart, pages,
     policies. Only the two templates carrying app blocks opt out. Add to
     this list whenever an app block is placed on a new template. */
  const APP_HOSTED = /^\/(products\/|$)|^\/?$/;

  /* In-place UI, not navigation: the bag drawer, quick view, the search
     field's own open/close, and any overlay dismiss. */
  const IN_PLACE = [
    '[data-cart-open]', '[data-quick-view]', '[data-ov-close]',
    '[data-search-open]', '[data-search-close]', '[data-buy-now]',
  ].join(',');

  const prevent = ({ el, href }) => {
    if (!href) return true;
    let u;
    try { u = new URL(href, location.href); } catch { return true; }
    if (BLOCKED.test(u.pathname)) return true;
    if (BLOCKED_EXT.test(u.pathname)) return true;
    if (u.searchParams.has('section_id')) return true;   // section rendering API
    if (el && el.closest && el.closest('.shopify-preview-bar,[data-barba-prevent]')) return true;

    /* Controls that open something in place must never start a page
       transition. The bag is an <a href="/cart"> so that it still works with
       JS off, but its own handler calls preventDefault and slides the drawer
       out instead — so without this, one click both opens the drawer AND
       slams a full-screen panel through a navigation to /cart. Anything that
       cancels its own default belongs on this list. */
    if (el && el.closest && el.closest(IN_PLACE)) return true;

    /* the review-widget templates: let the browser do a full load */
    if (APP_HOSTED.test(u.pathname)) return true;
    return false;
  };

  /* ---------- 3. re-initialising the page's own scripts ------------------ */
  const reviveHome = () => {
    if (!HOME_JS) return;
    /* The fluid cursor lives in leftovrz-home-chrome, which sits outside the
       swapped container, so a Barba-entered home page has no #cursor-dot for
       the script to drive. Put one back before it looks.

       The intro is deliberately NOT restored. It is a curtain-raiser for
       arriving at the site, not for every click on the logo, and
       leftovrz-home.js:616 already null-guards its absence. */
    if (!document.getElementById('cursor-dot')) {
      const dot = document.createElement('div');
      dot.id = 'cursor-dot';
      dot.setAttribute('aria-hidden', 'true');
      document.body.appendChild(dot);
    }
    const s = document.createElement('script');
    s.src = HOME_JS;
    document.body.appendChild(s);
  };

  /* A <script> that arrives inside a swapped container is inert. The browser
     only runs script elements it parsed itself or that were created with
     createElement, and Barba's containers are neither — they are set as
     innerHTML, which the HTML spec explicitly forbids from executing script.

     Several sections ship their own behaviour this way (the product gallery
     arrows, the Keep browsing arrows, the collection page's setup), so
     without this they work on a full page load and are dead on every
     client-side navigation after it. Replacing each node with a real one
     built here is what makes it run.

     type is checked so data blocks — application/json manifests and the
     like — are left exactly where they are rather than being re-created for
     no reason. */
  const runScripts = (root) => {
    if (!root) return;
    for (const old of root.querySelectorAll('script')) {
      const type = (old.getAttribute('type') || '').toLowerCase();
      if (type && !/javascript|^module$/.test(type)) continue;
      const fresh = document.createElement('script');
      for (const a of old.attributes) fresh.setAttribute(a.name, a.value);
      fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    }
  };

  const revive = (namespace) => {
    runScripts(document.querySelector('[data-barba="container"]'));
    if (typeof window.__lvGridFilters === 'function') window.__lvGridFilters();
    if (typeof window.__lvPicker === 'function') window.__lvPicker();
    if (typeof window.__lvBuyBar === 'function') window.__lvBuyBar();
    if (typeof window.__lvEngage === 'function') window.__lvEngage();

    /* No review-app re-initialisation here, deliberately.

       An earlier version called jdgm.initializeWidgets / init /
       customizeBadges after a swap. None of those exist — Judge.me's global
       exposes no public init at all — so every one of them was a silent
       no-op that looked like a fix. Measured on the live site: after a
       client-side navigation the widget's content stays inline-hidden, and
       even once it is revealed and the loader script re-injected, pressing
       "Write a review" opens nothing. The handlers bind once.

       Pages that host these widgets are excluded from the swap instead —
       see APP_HOSTED in the prevent rules above. */

    if (namespace === 'index') reviveHome();
  };

  /* Shopify's page-view beacon fires on document load. An SPA swap never
     reloads, so every page after the first would be invisible to analytics
     unless we say so ourselves. */
  const ping = () => {
    try {
      const A = window.ShopifyAnalytics;
      if (A && A.lib && typeof A.lib.page === 'function') {
        A.lib.page(null, { path: location.pathname, url: location.href, title: document.title });
      }
      if (typeof window.gtag === 'function') {
        gtag('event', 'page_view', { page_path: location.pathname, page_title: document.title });
      }
    } catch (e) { /* analytics must never break navigation */ }
  };

  /* The layout stamps template-<name> on <body>, but <body> is not swapped.
     Several rules key off it — `body:not(.template-index) .reveal` is the
     one that decides whether a section is visible at all — so it has to be
     rewritten by hand or the new page inherits the old page's visibility. */
  const setTemplateClass = (ns) => {
    document.body.className = document.body.className
      .replace(/\btemplate-\S+/g, '').trim() + ' template-' + ns;
  };

  /* Carry across the head bits that actually differ per page. Crawlers get
     the server-rendered document, so this is for the address bar, the tab,
     and anything reading the DOM after a client-side navigation. */
  const syncHead = (rawHtml) => {
    try {
      const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
      const t = doc.querySelector('title');
      if (t) document.title = t.textContent;
      for (const sel of ['link[rel="canonical"]', 'meta[name="description"]']) {
        const from = doc.querySelector(sel);
        const to = document.querySelector(sel);
        if (from && to) to.setAttribute(from.hasAttribute('href') ? 'href' : 'content',
          from.getAttribute('href') || from.getAttribute('content'));
      }
    } catch (e) {}
  };

  /* ---------- 4. transitions -------------------------------------------- */
  const CARD = '.card, .rail-card, [data-quick]';
  const cardOf = (trigger) =>
    trigger && trigger.nodeType === 1 && typeof trigger.closest === 'function'
      ? trigger.closest(CARD) : null;

  let flipState = null;

  const flip = {
    name: 'card-to-product',
    /* Decided from the trigger alone, never from `to: { namespace }`. A
       namespace rule makes Barba wait for the next document before it can
       even pick a transition, which means the click sits there doing
       nothing while the network works. A product card always points at a
       product, so the trigger is enough. */
    custom: ({ trigger }) => !!cardOf(trigger),

    async leave({ current, trigger }) {
      const card = cardOf(trigger);
      const img = card && card.querySelector('.card__media img, img');
      if (!img || reduceMQ.matches) { await cover(); return; }

      const r = img.getBoundingClientRect();
      const clone = img.cloneNode(true);
      clone.className = 'pt-flip';
      clone.removeAttribute('loading');
      clone.style.left = r.left + 'px';
      clone.style.top = r.top + 'px';
      clone.style.width = r.width + 'px';
      clone.style.height = r.height + 'px';
      document.body.appendChild(clone);
      flipState = { clone, r };

      /* The clone is the continuity, so the grid should simply recede rather
         than being slammed over. The body ground is the darkroom black, so
         what is left behind the shoe is the brand's own backdrop. */
      await settle(current.container.animate([{ opacity: 1 }, { opacity: 0 }],
        { duration: ms(RECEDE), easing: EASE, fill: 'forwards' }), ms(RECEDE));
    },

    async enter({ next }) {
      window.scrollTo(0, 0);
      const st = flipState; flipState = null;

      if (!st) { await uncover(); return; }

      const target = next.container.querySelector('.pgal__slide img');
      if (!target || reduceMQ.matches) {
        st.clone.remove();
        next.container.style.opacity = '';
        await uncover();
        return;
      }

      next.container.style.opacity = '0';
      await raf2();                                   // let layout settle
      const d = target.getBoundingClientRect();
      if (!d.width || !d.height) {                    // nothing to fly into
        st.clone.remove();
        next.container.style.opacity = '';
        return;
      }

      target.style.visibility = 'hidden';
      const dx = d.left - st.r.left, dy = d.top - st.r.top;
      const sx = d.width / st.r.width, sy = d.height / st.r.height;

      await Promise.all([
        settle(st.clone.animate([
          { transform: 'translate3d(0,0,0) scale(1,1)' },
          { transform: `translate3d(${dx}px,${dy}px,0) scale(${sx},${sy})` },
        ], { duration: ms(FLIP), easing: EASE, fill: 'forwards' }), ms(FLIP)),
        settle(next.container.animate([{ opacity: 0 }, { opacity: 1 }],
          { duration: ms(FLIP * 0.55), delay: ms(FLIP * 0.45), easing: 'linear', fill: 'forwards' }),
          ms(FLIP)),
      ]);

      target.style.visibility = '';
      next.container.style.opacity = '';
      st.clone.remove();
    },
  };

  const slam = {
    name: 'slam',
    async leave() { await cover(); },
    async enter() { window.scrollTo(0, 0); await uncover(); },
  };

  /* ---------- 5. wiring -------------------------------------------------- */
  barba.hooks.before(() => {
    document.documentElement.classList.add('is-transitioning');
    /* A menu left open across a navigation is a menu describing the page you
       just left. */
    const burger = document.querySelector('.nav__burger[aria-expanded="true"]');
    if (burger) burger.click();
  });

  barba.hooks.beforeEnter(({ next }) => {
    setTemplateClass(next.namespace);
    if (next.html) syncHead(next.html);
  });

  barba.hooks.after(({ next }) => {
    document.documentElement.classList.remove('is-transitioning');
    /* Belt and braces: if a transition bailed early, the flying clone and a
       faded container are what the shopper would be left looking at. */
    for (const el of document.querySelectorAll('.pt-flip')) el.remove();
    flipState = null;
    const c = document.querySelector('[data-barba="container"]');
    if (c) c.style.opacity = '';
    revive(next.namespace);
    ping();
    /* Send screen readers into the new document rather than leaving them
       parked where the old one used to be. */
    const main = document.getElementById('main');
    if (main) { main.setAttribute('tabindex', '-1'); main.focus({ preventScroll: true }); }
  });

  /* Prefetch is what makes the panel feel like a cut rather than a wait.
     Without it Barba only starts fetching once you have clicked, so the
     cover animation finishes and then you sit on yellow until the network
     answers. With it, links are fetched as they come into view during idle
     time and the click is served from cache.

     limit caps how many pages a long grid will pull, so a collection of
     fifty cards does not turn into fifty requests.

     Skipped on Data Saver and 2G/3G: home and product pages are full loads,
     so the cache dies with them and every visit re-pulls ~700KB of pages
     the shopper may never open. Barba still prefetches on hover/touch. */
  const conn = navigator.connection;
  const lean = !!conn && (conn.saveData || /2g|3g/.test(conn.effectiveType || ''));
  if (window.barbaPrefetch && !lean) {
    barba.use(window.barbaPrefetch, { limit: 25, timeout: 2000 });
  }

  barba.init({
    debug: false,
    timeout: 6000,          // a cold product page can outrun the 2s default
    preventRunning: true,   // ignore clicks while a transition is in flight
    prefetchIgnore: false,  // both of these must stay falsy or Prefetch.init()
    cacheIgnore: false,     // warns and returns without installing anything
    prevent,
    transitions: [flip, slam],
  });
})();
